import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges, ViewChild, output } from "@angular/core";
import { NgxPopperjsDirective, NgxPopperjsModule, NgxPopperjsPlacements, NgxPopperjsTriggers } from "ngx-popperjs";
import { FormsModule } from "@angular/forms";
import { AsyncPipe, NgClass, NgFor, NgIf } from "@angular/common";
import { RibbonButtonComponent } from "../../../../shared/buttons/ribbon-button/ribbon-button.component";
import { Chips, ChipsModule } from 'primeng/chips';
import { PopperHelper } from "@nts/std/utility";
import { IMaskModule } from "angular-imask";
import { MaskedPattern } from "imask";
import { OverlayOptions } from "primeng/api";
import { UICommandInterface } from "@nts/std/interfaces";

export const DEFAULT_CHIPS_SEPARATOR = ';';

export interface ChipsOptionsInterface<TOption = { code: string, description?: string; }> {
    separator?: string;
    optionsList?: TOption[];
    optionCodeField?: keyof TOption;
    optionDescriptionField?: keyof TOption;
    overlayOptions?: OverlayOptions;
}

export interface BaseChipsBoxMaskSettings {
    mask: string;
    hideMaskOnBlur?: boolean;
    validateMaskOnBlurFunc?: (valueToValidate: string) => boolean;
    overwrite?: boolean | 'shift';
    placeholderChar: string;
    lazy?: boolean;
    eager?: boolean;
}

@Component({
    selector: 'nts-base-chips-box',
    templateUrl: './base-chips-box.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./base-chips-box.component.scss'],
    standalone: true,
    imports: [
        FormsModule,
        NgxPopperjsModule,
        NgClass,
        NgIf,
        RibbonButtonComponent,
        AsyncPipe,
        NgFor,
        ChipsModule,
        IMaskModule
    ]
})
export class BaseChipsBoxComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {

    @Input() tabIndex = -1;
    @Input() isDisabled = false;
    @Input() value = [];
    @Input() isReadonly = false;
    @Input() placeholder = '';
    @Input() customClasses = '';
    @Input() errorList: string[] = [];
    @Input() showErrorTooltip = true;
    @Input() showErrorBorder = true;
    @Input() customCommandList: UICommandInterface[] = [];
    @Input() defaultBorderColor = null;
    @Input() activeBorderColor = null;
    @Input() hoverBorderColor = null;
    @Input() listenClickOutside = false;
    @Input() multiline = false;
    @Input() field = null;
    @Input() addOnTab = true;
    @Input() addOnBlur = true;
    @Input() maxEntries = null;
    @Input() chipsOptions = null;
    @Input() allowDuplicate = true;
    @Input() showClear = true;
    @Input() maskSettings: BaseChipsBoxMaskSettings;

    /**
     * Funzione per validate il chip prima di aggiungerlo
     * Default: null
     */
    @Input() validateBeforeAddFunc: (valueToVAlidate: string) => boolean = null;

    readonly onFinishEditing = output<void>();
    readonly onBlur = output<Event>();
    readonly onFocus = output<Event>();
    readonly onValueChange = output<any>();
    readonly viewReady = output<void>();
    isViewReady = false;

    @ViewChild(Chips, { static: true }) chipsBox: Chips;
    @ViewChild(NgxPopperjsDirective, { static: true }) popperError: NgxPopperjsDirective;

    ngxPopperjsTriggers = NgxPopperjsTriggers;
    ngxPopperjsPlacements = NgxPopperjsPlacements;
    overrideBorderColor = null;
    isActive = false;
    isHover = false;
    mask = null;
    chipsSeparator: string = null;

    private documentClickListener: any;
    private destroyed = false;

    constructor(
        public readonly el: ElementRef,
        private readonly renderer: Renderer2,
        private readonly zone: NgZone,
        private readonly cd: ChangeDetectorRef,
    ) {
        this.getMaskElement = this.getMaskElement.bind(this);
    }
    ngAfterViewInit(): void {

        // Faccio l'override di add item nel caso in cui devo validare il testo prima di aggiungerlo
        if (this.validateBeforeAddFunc && this.chipsBox.addItem) {
            const oldAddItem = this.chipsBox.addItem;
            this.chipsBox.addItem = (event: Event, item: string, preventDefault: boolean) => {
                if (!this.validateBeforeAddFunc(this.chipsBox.inputViewChild.nativeElement.value)) {
                    return;
                }
                oldAddItem.call(this.chipsBox, event, item, preventDefault);
            };
            this.chipsBox.addItem.bind(this.chipsBox);

        }

        this.updateChipsValues();
        this.cd.detectChanges();

        queueMicrotask(() => {
            if (!this.destroyed) {
                this.emitViewReady();
            }
        });
    }

    private emitViewReady(): void {
        this.isViewReady = true;
        this.viewReady.emit();
    }

    private updateChipsValues() {
        this.chipsBox.writeValue.call(this.chipsBox, this.value);
        this.chipsBox.value = this.value;
        this.chipsBox.onModelChange.call(this.chipsBox, (value) => this.valueChange(value));
        this.chipsBox.registerOnTouched.call(this.chipsBox, () => {
            this.value = this.chipsBox.value;
            this.valueChange(this.chipsBox.value);
            this.cd.detectChanges();
        });

    }

    ngOnInit() {
        if (this.listenClickOutside) {
            this.bindDocumentClickListener();
        }
        this.initMaskSettings();
        this.handleOverridesColors();

        if (!this.chipsOptions) {
            this.chipsOptions = this.getDefaultChipsOptions();
        }
        this.checkChipsOptions();
    }

    getDefaultChipsOptions(): ChipsOptionsInterface {
        return {
            separator: DEFAULT_CHIPS_SEPARATOR
        };
    }

    initMaskSettings(): any {

        const maskSettings = this.maskSettings;

        if (maskSettings) {
            const mask = new MaskedPattern({
                mask: maskSettings.mask,
                overwrite: maskSettings.overwrite,
                lazy: maskSettings.lazy,
                placeholderChar: maskSettings.placeholderChar,
                eager: maskSettings.eager
            });
            this.mask = { mask };
        }
    }

    getMaskElement(elementRef, directiveRef) {
        return this.chipsBox.inputViewChild.nativeElement;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['defaultBorderColor'] || changes['activeBorderColor'] || changes['hoverBorderColor']) {
            this.handleOverridesColors();
        }

        if (changes['errorList']) {
            this.checkPopper();
        }

        if (changes['chipsOptions']) {
            this.checkChipsOptions();
        }

        if (changes['value']) {
            this.updateChipsValues();
            this.cd.detectChanges();
        }
    }

    checkChipsOptions() {
        if (this.chipsOptions?.separator?.length > 0) {
            this.chipsSeparator = this.chipsOptions.separator;
        }
    }

    focus(e: Event): void {
        this.isActive = true;
        if (this.maskSettings?.hideMaskOnBlur) {
            this.mask.mask.updateOptions({ lazy: this.maskSettings.lazy });
        }
        this.handleOverridesColors();
        this.cd.detectChanges();
        this.onFocus.emit(e);
    }

    blur(e: Event) {
        if (this.maskSettings?.hideMaskOnBlur) {
            this.mask.mask.updateOptions({ lazy: true });
            if (this.maskSettings?.validateMaskOnBlurFunc && !this.maskSettings?.validateMaskOnBlurFunc(this.chipsBox.inputViewChild.nativeElement.value)) {
                this.chipsBox.inputViewChild.nativeElement.value = '';
            }
        }
        this.popperError?.hide();
        this.cd.detectChanges();
        this.onBlur.emit(e);
    }

    ngOnDestroy() {
        this.destroyed = true;
        this.unbindDocumentClickListener();
    }

    unbindDocumentClickListener() {
        if (this.documentClickListener) {
            this.documentClickListener();
            this.documentClickListener = null;
        }
    }

    mouseEnter(e) {
        this.isHover = true;
        this.handleOverridesColors();
    }

    mouseLeave(e) {
        this.isHover = false;
        this.handleOverridesColors();
    }

    handleOverridesColors() {
        if (!this.defaultBorderColor || !this.activeBorderColor || !this.hoverBorderColor) {
            // devono essere impostate tutte e tre le variabili
            return;
        }

        this.overrideBorderColor = this.defaultBorderColor;
        if (this.isActive && !this.isDisabled) {
            this.overrideBorderColor = this.activeBorderColor;
        }
        if (this.isHover && !this.isDisabled) {
            this.overrideBorderColor = this.hoverBorderColor;
        }
    }

    bindDocumentClickListener() {
        if (!this.documentClickListener) {
            this.zone.runOutsideAngular(() => {
                const documentTarget: any = this.el ? this.el.nativeElement.ownerDocument : 'document';

                this.documentClickListener = this.renderer.listen(documentTarget, 'click', (event) => {
                    if (this.isOutsideClicked(event)) {
                        this.zone.run(() => {
                            this.onFinishEditing.emit();

                            this.cd.markForCheck();
                        });
                    }

                });
            });
        }
    }

    isOutsideClicked(event: Event) {
        return !(this.el.nativeElement.isSameNode(event.target) || this.el.nativeElement.contains(event.target));
    }

    valueChange(value: any): void {
        this.onValueChange.emit(value);
    }

    private checkPopper() {
        if (this.popperError && (this.errorList == null || this.errorList.length === 0)) {
            PopperHelper.hide(this.popperError);
        }
        // if (this.popperInfo && this.errorList?.length > 0) {
        //     PopperHelper.hide(this.popperInfo);
        // }
    }
}
